import type { GroupType } from "@/types/entities/approval-groups";
import { cn } from "@/utils/theme";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  Checkbox,
  DropdownMenuContent,
  DropdownMenuTrigger,
  Icon,
  Input,
  ScrollArea,
} from "@elaniintc/goes-design-system";
import { useInView } from "react-intersection-observer";
import { DropdownMenu } from "@radix-ui/react-dropdown-menu";

interface GroupSelectorProps {
  groups: GroupType[] | undefined;
  selectedGroup?: GroupType | null;
  onGroupChange: (group: GroupType) => void; // ✅ toggle (add/remove) lo haces fuera
  className?: string;
  placeholder?: string;

  onLoadMore?: () => void;
  hasMore?: boolean;
  isLoadingMore?: boolean;

  selectedGroupsList: GroupType[];
}

export const GroupSelector = ({
  onGroupChange,
  groups,
  placeholder = "Seleccionar grupos",
  className,
  onLoadMore,
  hasMore = false,
  isLoadingMore = false,
  selectedGroupsList,
}: GroupSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const searchInputRef = useRef<HTMLInputElement | null>(null);

  const focusSearch = () => {
    requestAnimationFrame(() => {
      searchInputRef.current?.focus();
    });
  };

  useEffect(() => {
    if (!open) setSearchQuery("");
  }, [open]);

  const normalizedQuery = useMemo(
    () => searchQuery.trim().toLowerCase(),
    [searchQuery],
  );

  const groupsSelectedIds = useMemo(
    () => (selectedGroupsList ?? []).map((g) => g.id),
    [selectedGroupsList],
  );

  const filteredGroups: GroupType[] = useMemo(() => {
    const list = groups ?? [];
    if (!normalizedQuery) return list;
    return list.filter((g) => g.name.toLowerCase().includes(normalizedQuery));
  }, [groups, normalizedQuery]);

  const canLoadMore =
    Boolean(onLoadMore) && hasMore && !isLoadingMore && !normalizedQuery;

  const { ref: sentinelRef, inView } = useInView({
    root: null,
    rootMargin: "0px",
    threshold: 0,
  });

  useEffect(() => {
    if (inView && canLoadMore) onLoadMore?.();
  }, [inView, canLoadMore, onLoadMore]);

  if (!groups) return null;

  const toggleById = (id: string) => {
    const group = groups.find((g) => g.id === id);
    if (!group) return;

    onGroupChange(group);

    setOpen(true);
    focusSearch();
  };

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (v) focusSearch();
      }}
    >
      <DropdownMenuTrigger
        asChild
        className={cn("min-w-0 w-full flex rounded-lg", className)}
      >
        <Button
          variant="tertiary"
          className={cn(
            "flex justify-start font-normal items-center group min-w-0 relative gap-2",
          )}
        >
          <div className="min-w-0 flex flex-1 gap-3 overflow-hidden">
            <div className="flex items-center">
              <Badge
                shape="pill"
                variant="info"
                className="size-6 shrink-0 rounded-full flex items-center justify-center"
              >
                <Icon type="fa" name="users" className="text-xs" />
              </Badge>
            </div>

            {selectedGroupsList?.length ? (
              <span className="flex-1 max-w-[90%] truncate text-left">
                {selectedGroupsList.map((g) => g.name).join(", ")}
              </span>
            ) : (
              <p className="flex-1 truncate text-left text-body-quiet">
                {placeholder}
              </p>
            )}

            <Icon
              type="far"
              name="angle-down"
              className="group-data-[state=open]:rotate-180 transition-transform duration-200 group-data-[state=open]:text-body absolute right-3 top-1/2 transform -translate-y-1/2"
            />
          </div>
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        className={cn(
          "flex flex-col rounded-lg border-[0.5px] border-neutral bg-neutral-object text-body-loud font-semibold shadow-neutral-xl w-[var(--radix-dropdown-menu-trigger-width)] pt-0",
        )}
        side="bottom"
        align="start"
      >
        <div className="sticky top-0 z-10 border-b border-neutral-quiet bg-neutral-object p-3">
          <div className="relative">
            <Icon
              type="far"
              name="search"
              className="absolute left-3 top-1/2 -translate-y-1/2 text-body"
            />
            <Input
              ref={searchInputRef}
              variant="sm"
              className="text-sm w-full font-normal flex-auto pl-9"
              placeholder="Buscar grupo..."
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                focusSearch();
              }}
              onKeyDown={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
        </div>

        {/* ✅ Solo la lista hace scroll */}
        <ScrollArea className="max-h-66" role="listbox" aria-label="Groups">
          {filteredGroups.length === 0 ? (
            <div className="px-3 py-2 text-body-quiet select-none">
              No hay resultados
            </div>
          ) : (
            filteredGroups.map((group) => {
              const isSelected = groupsSelectedIds.includes(group.id);

              return (
                <div
                  key={group.id}
                  role="menuitem"
                  className="rounded-lg cursor-pointer text-body relative flex items-center gap-2 py-2 px-3 text-sm outline-hidden select-none hover:bg-neutral-object-accent-silent focus:bg-accent focus:text-accent-foreground data-[disabled]:pointer-events-none data-[disabled]:opacity-50"
                  onClick={() => toggleById(group.id)}
                >
                  <div className="w-full flex items-center gap-3">
                    <Checkbox checked={isSelected} />
                    <div className="flex gap-2 items-center flex-1 min-w-0">
                      <span className="text-body truncate">{group.name}</span>
                    </div>
                    <Badge
                      shape="pill"
                      size="sm"
                      variant="neutral"
                      className="shrink-0"
                    >
                      {group.members_count ?? 0}
                      <Icon
                        type="fas"
                        name="user"
                        className="text-body-quiet ml-1"
                      />
                    </Badge>
                  </div>
                </div>
              );
            })
          )}

          {canLoadMore && (
            <div
              ref={sentinelRef}
              className="px-3 py-2 text-body-quiet select-none"
            >
              {isLoadingMore && (
                <div className="flex justify-center items-center py-0.5 px-3 gap-4">
                  <Icon
                    type="far"
                    name="spinner-third"
                    className="animate-spin text-xl text-body"
                  />
                </div>
              )}
            </div>
          )}
        </ScrollArea>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
